/**
 * DuplicateActionModal — shown immediately after the wizard resumes a
 * duplicated study. A clean vertical list of selectable action rows
 * (audience / questionnaire / review & re-launch). Each row: leading visual
 * (audience cover thumbnail or themed icon), title + description, trailing
 * chevron. Matches the platform design language — semantic tokens,
 * font-display heading, framer-motion stagger entrance inside a Dialog.
 */

import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronRight, MessageSquare, Rocket, Users } from "@digital-twins/design-system/icons";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { resolveAudienceCanonicalCoverUrl } from "@/pages/AudiencesPage/utils/audience-presentation";
import { cardGridVariants, cardVariants, headlineVariants } from "./goals/motion/tokens";
import type { Population } from "@/types/routes/tests";

export type DuplicateAction = "audience" | "questionnaire" | "launch";

interface DuplicateActionModalProps {
  open: boolean;
  population: Population | undefined;
  studyName: string | null;
  onChoose: (action: DuplicateAction) => void;
  /** Dismiss the modal (X / explicit close) without choosing an action. */
  onClose: () => void;
}

export function DuplicateActionModal({
  open,
  population,
  studyName,
  onChoose,
  onClose,
}: DuplicateActionModalProps) {
  const { t } = useTranslation("app");
  const shouldReduceMotion = useReducedMotion();
  const coverUrl = resolveAudienceCanonicalCoverUrl(population ?? null);
  const hasAudience = population !== undefined;

  function animated(variants: Parameters<typeof motion.div>[0]["variants"]) {
    if (shouldReduceMotion) return {};
    return { variants, initial: "hidden", animate: "visible" } as const;
  }

  const rows: Array<{
    id: DuplicateAction;
    title: string;
    description: string;
    leading: React.ReactNode;
    disabled?: boolean;
    disabledReason?: string;
  }> = [
    {
      id: "audience",
      title: t("creation.changeAudience"),
      description: t("creation.changeAudienceDesc"),
      leading: coverUrl ? (
        <img
          src={coverUrl}
          alt=""
          aria-hidden
          className="h-full w-full rounded-[10px] object-cover"
        />
      ) : (
        <Users className="h-5 w-5 text-primary" />
      ),
    },
    {
      id: "questionnaire",
      title: t("creation.editQuestionnaire"),
      description: t("creation.editQuestionnaireDesc"),
      leading: <MessageSquare className="h-5 w-5 text-primary" />,
    },
    {
      id: "launch",
      title: t("creation.relaunch"),
      description: t("creation.relaunchDesc"),
      leading: <Rocket className={cn("h-5 w-5", hasAudience ? "text-primary" : "text-muted-foreground")} />,
      disabled: !hasAudience,
      disabledReason: t("creation.selectAudienceFirst"),
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="flex max-w-[440px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:p-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">
          {t("creation.duplicateAriaTitle")}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t("creation.duplicateAriaBody")}
        </DialogDescription>

        <div className="w-full p-6">
          {/* Headline */}
          <motion.div {...animated(headlineVariants)} className="mb-5">
            <h2 className="font-display text-[22px] leading-tight text-foreground">
              {t("creation.duplicateTitle")}
            </h2>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {studyName?.trim() || t("creation.newStudy")}
            </p>
          </motion.div>

          {/* Action rows */}
          <motion.div {...animated(cardGridVariants)} className="flex flex-col gap-2">
            {rows.map((row) => (
              <motion.button
                key={row.id}
                type="button"
                variants={shouldReduceMotion ? undefined : cardVariants}
                onClick={() => !row.disabled && onChoose(row.id)}
                disabled={row.disabled}
                aria-disabled={row.disabled}
                data-testid={`duplicate-action-${row.id}`}
                className={cn(
                  "group flex w-full items-center gap-3.5 rounded-xl border border-border bg-card-inset p-3 text-left",
                  "transition-[border-color,box-shadow,transform] duration-200 ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  row.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
                )}
              >
                {/* Leading visual */}
                <span
                  className={cn(
                    "flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-[10px]",
                    row.disabled ? "bg-muted ring-1 ring-border" : "bg-primary/10 ring-1 ring-primary/15",
                  )}
                >
                  {row.leading}
                </span>

                {/* Text */}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className={cn("font-display text-[15px] leading-tight", row.disabled ? "text-muted-foreground" : "text-foreground")}>
                    {row.title}
                  </span>
                  <span className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">
                    {row.disabled && row.disabledReason ? row.disabledReason : row.description}
                  </span>
                </span>

                {/* Trailing chevron */}
                {!row.disabled && (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary" />
                )}
              </motion.button>
            ))}
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
